import { useState, useRef, useEffect } from "react";
import { serviceCategories } from "@/mocks/nailServices";

export default function ServicesSection() {
  const [activeTab, setActiveTab] = useState(0);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const currentCategory = serviceCategories[activeTab];

  return (
    <section id="services" className="py-20 lg:py-28 bg-[#F5F0EB]" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">

          {/* Left — Text + Image */}
          <div
            className={`w-full lg:w-[42%] transition-all duration-700 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6"
            }`}
          >
            <p className="font-['DM_Sans'] text-xs font-medium tracking-[0.3em] uppercase text-[#C9A87C] mb-3">
              Menu
            </p>
            <h2 className="font-['Cormorant_Garamond'] text-5xl lg:text-6xl font-light text-[#1A1A1A] leading-tight mb-6">
              あなただけの<br />
              <span className="italic text-[#C9A87C]">ネイルデザイン</span>
            </h2>
            <p className="font-['Noto_Sans_JP'] text-sm text-[#6B6B6B] leading-relaxed mb-8">
              丁寧なカウンセリングから始まる施術で、お客様一人ひとりの
              ライフスタイルやご要望に合わせた最高の仕上がりをご提供します。
              高品質な国内外ブランドのジェルカラーのみを使用。
            </p>

            {/* Salon Image */}
            <div className="relative rounded-[20px] overflow-hidden h-[300px] lg:h-[360px]">
              <img
                src="https://public.readdy.ai/ai/img_res/bcb6108ae519195dc4bb48b2ea0c1484.jpg"
                alt="サロン施術風景"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#3D2E2A]/50 to-transparent"></div>
              <div className="absolute bottom-5 left-5 right-5">
                <div className="inline-flex items-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full">
                  <i className="ri-shield-check-line text-[#C9A87C] text-sm"></i>
                  <span className="font-['DM_Sans'] text-xs font-medium text-[#3D2E2A]">
                    プロフェッショナルケア
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right — Service Menu */}
          <div
            className={`w-full lg:flex-1 transition-all duration-700 delay-200 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-6"
            }`}
          >
            {/* Tab Switcher */}
            <div className="flex items-center gap-1 bg-[#EDE8E3] rounded-full px-1 py-1 mb-8 w-fit">
              {serviceCategories.map((cat, i) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveTab(i)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-all duration-300 cursor-pointer whitespace-nowrap font-['DM_Sans'] ${
                    activeTab === i
                      ? "bg-[#3D2E2A] text-white"
                      : "text-[#6B6B6B] hover:text-[#3D2E2A]"
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.title}
                </button>
              ))}
            </div>

            {/* Service Items */}
            <div className="space-y-3">
              {currentCategory.services.map((service, i) => (
                <div
                  key={service.id}
                  className="bg-white rounded-[16px] p-5 hover:-translate-y-0.5 transition-transform duration-200 cursor-default"
                  style={{ animationDelay: `${i * 80}ms` }}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-['Noto_Sans_JP'] text-sm font-medium text-[#1A1A1A]">
                          {service.name}
                        </p>
                        {service.tag && (
                          <span className="bg-[#FFF0E6] text-[#C9A87C] text-[10px] font-medium px-2 py-0.5 rounded-full font-['DM_Sans']">
                            {service.tag}
                          </span>
                        )}
                      </div>
                      <p className="font-['Noto_Sans_JP'] text-xs text-[#9E9189]">
                        {service.description}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#3D2E2A]">
                        {service.price}
                      </p>
                      <p className="font-['DM_Sans'] text-xs text-[#9E9189] mt-0.5">
                        {service.duration}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Note */}
            <div className="mt-6 bg-[#FFF8F2] border border-[#F0E4D4] rounded-[12px] p-4">
              <p className="font-['Noto_Sans_JP'] text-xs text-[#9E9189] leading-relaxed">
                <i className="ri-information-line text-[#C9A87C] mr-1"></i>
                表示価格はすべて税込みです。デザインによって料金が変動する場合がございます。
                詳細はカウンセリング時にご確認ください。
              </p>
            </div>

            {/* Book CTA */}
            <button
              onClick={() => {
                const el = document.querySelector("#booking");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="mt-6 flex items-center gap-2 bg-[#3D2E2A] text-white text-sm font-medium px-8 py-3.5 rounded-full hover:bg-[#C9A87C] transition-colors duration-300 cursor-pointer whitespace-nowrap font-['DM_Sans'] tracking-wider"
            >
              このメニューで予約する
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
